import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FaHome } from "react-icons/fa";
import {
  FaBars,
  FaBoxOpen,
  FaCashRegister,
  FaChartPie,
  FaClipboardList,
  FaLayerGroup,
  FaRegCircleUser,
  FaTags,
  FaUsersGear,
  FaXmark,
} from "react-icons/fa6";
import { useSelector } from "react-redux";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import ROLE from "../common/role";

const getAdminRoute = (path) => `/admin-panel/${path}`;

const AdminPanel = () => {
  const { user, isLoading } = useSelector((state) => state?.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && user?.role !== ROLE.ADMIN) {
      navigate("/");
    }
  }, [user, isLoading, navigate]);

  // Close sidebar on route change (for mobile)
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const isActiveRoute = (path) => {
    const fullPath = getAdminRoute(path);
    return (
      location.pathname === fullPath ||
      location.pathname.startsWith(`${fullPath}/`)
    );
  };

  const navItems = [
    { label: "Dashboard", path: "dashboard", icon: <FaChartPie /> },
    { label: "Analytics", path: "analytics", icon: <FaChartPie /> },
    { label: "Reports", path: "reports", icon: <FaClipboardList /> },
    { label: "All Users", path: "all-users", icon: <FaUsersGear /> },
    { label: "Categories", path: "category", icon: <FaLayerGroup /> },
    { label: "Brands", path: "brand", icon: <FaTags /> },
    { label: "Products", path: "all-products", icon: <FaBoxOpen /> },
    { label: "Orders", path: "all-orders", icon: <FaClipboardList /> },
    { label: "Coupons", path: "coupons", icon: <FaTags /> },
    { label: "Reviews", path: "reviews", icon: <FaClipboardList /> },
    { label: "POS System", path: "pos", icon: <FaCashRegister /> },
    { label: "Logs", path: "logs", icon: <FaClipboardList /> },
  ];

  const SidebarContent = () => (
    <>
      {/* Admin Profile */}
      <div className="px-8 mb-12 flex flex-col items-center text-center">
        <div className="w-24 h-24 mb-4 rounded-full border-4 border-surface-muted p-1 ring-2 ring-accent/10 relative group overflow-hidden">
          {user?.profilePic ? (
            <img
              src={user?.profilePic}
              className="w-full h-full rounded-full object-cover transition-transform duration-500 group-hover:scale-110"
              alt={user?.name}
            />
          ) : (
            <div className="w-full h-full bg-surface-muted rounded-full flex items-center justify-center text-gray-400">
              <FaRegCircleUser size={40} />
            </div>
          )}
          <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <h3 className="text-xl font-black text-primary tracking-tight capitalize">
          {user?.name}
        </h3>
        <p className="text-xs font-bold text-accent uppercase tracking-widest mt-1">
          {user?.role} Access
        </p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-1 pb-8">
        {navItems.map((item) => {
          const isActive = isActiveRoute(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`
                                flex items-center gap-4 px-6 py-4 rounded-2xl font-bold text-sm transition-all relative group
                                ${
                                  isActive
                                    ? "bg-primary text-white shadow-xl shadow-primary/20"
                                    : "text-gray-500 hover:bg-surface-muted hover:text-primary"
                                }
                            `}
            >
              <span
                className={`text-lg ${isActive ? "text-accent" : "text-gray-400 group-hover:text-primary transition-colors"}`}
              >
                {item.icon}
              </span>
              {item.label}
              {isActive && (
                <span
                  className="absolute right-4 w-1.5 h-1.5 bg-accent rounded-full"
                  aria-hidden="true"
                />
              )}
            </Link>
          );
        })}

        {/* Divider */}
        <div className="my-4 border-t border-surface-border mx-6"></div>

        {/* Back to Shop Link */}
        <Link
          to="/"
          className="flex items-center gap-4 px-6 py-4 rounded-2xl font-bold text-sm text-gray-500 hover:bg-surface-muted hover:text-primary transition-all group"
        >
          <span className="text-lg text-gray-400 group-hover:text-primary transition-colors">
            <FaHome />
          </span>
          Back to Shop
        </Link>
      </nav>
    </>
  );

  return (
    <div className="min-h-screen bg-surface-muted flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden bg-white/80 backdrop-blur-xl border-b border-surface-border px-4 py-4 flex items-center justify-between sticky top-0 z-[100]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-2xl flex items-center justify-center text-white shadow-lg shadow-primary/20">
            <FaUsersGear className="text-2xl" />
          </div>
          <div>
            <span className="block font-black text-gray-800 text-lg leading-none tracking-tight">
              Shalimar Shop
            </span>
            <div className="flex items-center gap-1 mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-none">
                Admin Panel
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="p-3 bg-gray-100 rounded-xl text-gray-600 hover:bg-gray-200 active:scale-95 transition-all"
        >
          <FaBars className="text-2xl" />
        </button>
      </div>

      {/* Backdrop for mobile */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[110] md:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Mobile Drawer */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 left-0 h-full w-[85%] max-w-[320px] bg-white z-[120] shadow-2xl flex flex-col md:hidden"
          >
            <div className="p-6 flex justify-end">
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-surface-muted text-gray-500 active:scale-95 transition-transform"
              >
                <FaXmark className="text-xl" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <SidebarContent />
            </div>
            <div className="p-6 border-t border-surface-border bg-surface-muted/30">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 text-center">
                Admin v2.0 • Premium Access
              </p>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex w-72 bg-white border-r border-surface-border flex-col pt-6 shadow-premium sticky top-0 h-screen overflow-y-auto custom-scrollbar">
        <SidebarContent />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 min-w-0">
        <div className="p-4 md:p-6 pb-24 md:pb-6 min-h-full">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation - Common Quick Actions */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-surface-border h-20 px-6 py-2 flex items-center justify-between z-[90] shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.05)]">
        <Link
          to="dashboard"
          className={`flex flex-col items-center gap-1 transition-all ${isActiveRoute("dashboard") ? "text-primary scale-110" : "text-gray-400"}`}
        >
          <div
            className={`p-2 rounded-xl ${isActiveRoute("dashboard") ? "bg-primary text-white shadow-lg shadow-primary/20" : ""}`}
          >
            <FaChartPie className="text-xl" />
          </div>
          <span className="text-[8px] font-black uppercase tracking-widest">
            Status
          </span>
        </Link>
        <Link
          to="all-products"
          className={`flex flex-col items-center gap-1 transition-all ${isActiveRoute("all-products") ? "text-primary scale-110" : "text-gray-400"}`}
        >
          <div
            className={`p-2 rounded-xl ${isActiveRoute("all-products") ? "bg-primary text-white shadow-lg shadow-primary/20" : ""}`}
          >
            <FaBoxOpen className="text-xl" />
          </div>
          <span className="text-[8px] font-black uppercase tracking-widest">
            Stock
          </span>
        </Link>
        <Link to="pos" className="flex flex-col items-center -mt-10 group">
          <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center text-white shadow-2xl shadow-accent/40 border-4 border-white active:scale-95 transition-all group-hover:rotate-12">
            <FaCashRegister className="text-2xl" />
          </div>
          <span className="text-[8px] font-black uppercase tracking-widest mt-2 text-accent">
            POS
          </span>
        </Link>
        <Link
          to="all-orders"
          className={`flex flex-col items-center gap-1 transition-all ${isActiveRoute("all-orders") ? "text-primary scale-110" : "text-gray-400"}`}
        >
          <div
            className={`p-2 rounded-xl ${isActiveRoute("all-orders") ? "bg-primary text-white shadow-lg shadow-primary/20" : ""}`}
          >
            <FaClipboardList className="text-xl" />
          </div>
          <span className="text-[8px] font-black uppercase tracking-widest">
            Orders
          </span>
        </Link>
        {/* <button
          onClick={() => setIsSidebarOpen(true)}
          className="flex flex-col items-center gap-1 text-gray-400"
        >
          <div className="p-2 rounded-xl">
            <FaBars className="text-xl" />
          </div>
          <span className="text-[8px] font-black uppercase tracking-widest">
            Menu
          </span>
        </button> */}
      </nav>
    </div>
  );
};

export default AdminPanel;
